import type { CancellationPolicy } from './settings';

// The three windows §13 measures in hours before arrival, as one word each.
export const REFUND_TIERS = ['full', 'partial', 'none'] as const;

export type RefundTier = (typeof REFUND_TIERS)[number];

// Viva's standard rate, recorded in plans/active/002-payment-provider-decision.
// A refund costs the venue the original transaction's fee plus €0.30. §13
export const PROCESSING_RATE_BPS = 219;

export const PROCESSING_FIXED_CENTS = 24;

export const REFUND_FIXED_CENTS = 30;

export const MS_PER_HOUR = 3_600_000;

// What the provider takes for a payment that is then given back. §13
export function processingCostCents(paidCents: number): number {
  if (paidCents === 0) return 0;
  const original = Math.round((paidCents * PROCESSING_RATE_BPS) / 10_000) + PROCESSING_FIXED_CENTS;
  return original + REFUND_FIXED_CENTS;
}

export function hoursBefore(arrivalMs: number, nowMs: number): number {
  return (arrivalMs - nowMs) / MS_PER_HOUR;
}

// Full outside the first window, nothing inside the last, the venue's own
// percentage between them. One table, both seeded venues. §13
export function refundTier(hours: number, policy: CancellationPolicy): RefundTier {
  if (hours >= policy.fullHours) return 'full';
  return hours > policy.noneHours ? 'partial' : 'none';
}

export function refundPct(tier: RefundTier, policy: CancellationPolicy): number {
  if (tier === 'full') return 100;
  return tier === 'none' ? 0 : policy.partialPct;
}

export type RefundQuote = {
  readonly tier: RefundTier;
  readonly pct: number;
  readonly paidCents: number;
  readonly grossCents: number;
  readonly feeCents: number;
  readonly refundCents: number;
};

// The guest bears the processing cost, so the quote states it before they
// decide, and the email repeats it. A refund never goes below nothing. §13
export function refundQuote(
  paidCents: number,
  hours: number,
  policy: CancellationPolicy,
): RefundQuote {
  const tier = refundTier(hours, policy);
  const pct = refundPct(tier, policy);
  const grossCents = Math.round((paidCents * pct) / 100);
  const feeCents = grossCents === 0 ? 0 : Math.min(grossCents, processingCostCents(paidCents));
  return { tier, pct, paidCents, grossCents, feeCents, refundCents: grossCents - feeCents };
}
